import { A, G, S } from '@mobily/ts-belt';
import { useRouter, NextRouter } from 'next/router';
import { useCallback } from 'react';
export type WardrobeParams = {
  page: number;
  showAvailableItems: boolean;
};
export type WardrobeParam = keyof WardrobeParams;
type SetWardrobeParam = <WardrobeParamsKey extends WardrobeParam>(name: WardrobeParamsKey, value: WardrobeParams[WardrobeParamsKey]) => void;
type SetWardrobePageNumberParam = (pageCount: number) => void;
type SetWardrobePage = (name: string) => void;
export type UseWardrobeParamReturn = {
  isReady: boolean;
  reload: NextRouter['reload'];
  /**
   * Returns current page under /wardrobe route
   */
  wardrobePage: string;
  /**
   * Returns an object containing query params from url
   */
  wardrobeParams: WardrobeParams;
  /**
   * Use `setWardrobeParam` when setting showAvailableItems, excluding `page`.
   */
  setWardrobeParam: SetWardrobeParam;
  /**
   * Use `setWardrobePage` to leverage next router to navigate
   * between the 'Bought' and 'Saved' pages of the wardrobe experience.
   */
  setWardrobePage: SetWardrobePage;
  /**
   * Use `setWardrobePageNumberParam` to leverage next router to reset the page number
   * in the URL query params.
   */
  setWardrobePageNumberParam: SetWardrobePageNumberParam;
};
type WardrobeParamValue = WardrobeParams[WardrobeParam];
const normalizeShowAvailableItems = (value: string | string[] | undefined): boolean => {
  if (G.isUndefined(value)) {
    return true;
  }
  if (G.isString(value) && S.isNotEmpty(value)) {
    return value === 'true';
  }
  if (G.isArray(value) && A.isNotEmpty(value)) {
    return value[0] === 'true';
  }
  return true;
};

/**
 * Parses the `page` string query param into a number.
 * If page is not a valid number, returns `1` by default.
 */
const parsePageQueryParam = (page: string | string[] | undefined): number => {
  let validPage = 1;
  if (G.isUndefined(page)) {
    validPage = 1;
  }
  if (G.isString(page)) {
    const parsedPage = parseInt(page, 10);

    // Guard against `parseInt` returning `NaN` when page is an invalid number string, e.g., "foo", "-3", etc.
    if (G.isNumber(parsedPage) && parsedPage > 0) {
      validPage = parsedPage;
    }
  }
  return validPage;
};

/**
 * Getter function to return an object containing wardrobe parameters based on query url
 */
const getWardrobeParams = (query: NextRouter['query']) => {
  const {
    showAvailableItems,
    page
  } = query;
  const wardrobeParams: WardrobeParams = {
    showAvailableItems: normalizeShowAvailableItems(showAvailableItems),
    page: parsePageQueryParam(page)
  };
  return wardrobeParams;
};

/**
 * Function that controls setting the wardrobe params value in the url. For
 * any other wrong param or value it will clear the query object. It currently accepts
 * showAvailableItems and disableAvailableItemsToggle
 */
const handleSetAvailableItemsWardrobeParam = (name: WardrobeParam, value: WardrobeParamValue, push: NextRouter['push']) => {
  if (name === 'showAvailableItems' && G.isBoolean(value)) {
    push({
      query: {
        showAvailableItems: value,
        page: '1'
      }
    }, undefined, {
      scroll: true,
      shallow: true
    });
  } else {
    push({
      query: {}
    }, undefined, {
      scroll: true,
      shallow: true
    });
  }
};

/**
 * Function that controls setting the `page` value in the url and persist the original
 * query params.
 */
const handleSetWardrobePageNumberParam = (pageCount: number, push: NextRouter['push'], query: NextRouter['query']) => {
  push({
    query: {
      ...query,
      page: pageCount
    }
  });
};

/**
 * Function that controls the wardrobe path, navigating between
 * 'saved-items' and 'bought-items'.
 */
const handleSetWardrobePage = (name: string, push: NextRouter['push']) => {
  if (name === 'saved-items') {
    /*
    We want to reset the query params to a default state of
    '{ showAvailableItems: true, page: '1' }' each time a user
    navigates to 'saved-items'
     */
    push({
      query: {
        showAvailableItems: true,
        page: '1'
      },
      pathname: name
    });
  } else {
    push({
      pathname: name
    });
  }
};

/**
 * Gets the path and returns whether it is saved-items or bought
 */
const getWardrobePage = (asPath: NextRouter['asPath']): string => {
  const [WardrobeRoot, WardrobePage] = asPath.substring(1).replace('?', '/').split('/');
  if (WardrobeRoot === 'wardrobe') {
    return WardrobePage;
  }
  return '';
};

/**
 * the `useWardrobeParams` hook uses the `next/router` query object
 *
 * Provides the `setWardrobeParam` helper to add or update the showAvailableItems param.
 * Provides the `setWardrobeParams` helper to add multiple query params.
 * Provides the `deleteWardrobeParam` helper to remove one param.
 * Provides the `wardrobePage` helper to return current page value
 * Provides the `setWardrobePage` to navigate between the 'Bought' and 'Saved' pages.
 * Provides the `setWardrobePageNumberParam` to reset the page number in the URL query params.
 */
export const useWardrobeParams = (): UseWardrobeParamReturn => {
  const {
    isReady,
    query,
    asPath,
    push,
    reload
  } = useRouter();
  const setWardrobeParam = useCallback<SetWardrobeParam>((name, value) => handleSetAvailableItemsWardrobeParam(name, value, push), [push]);
  const setWardrobePageNumberParam = useCallback<SetWardrobePageNumberParam>(pageCount => handleSetWardrobePageNumberParam(pageCount, push, query), [push, query]);
  const setWardrobePage = useCallback<SetWardrobePage>(name => handleSetWardrobePage(name, push), [push]);
  return {
    isReady,
    reload,
    wardrobePage: getWardrobePage(asPath),
    setWardrobePage,
    setWardrobePageNumberParam,
    wardrobeParams: getWardrobeParams(query),
    setWardrobeParam
  };
};